/**
 * VotingHeader Component
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 2: Create VotingHeader component
 *
 * Header for voting page with back link, proposal title, and countdown timer.
 * Timer shows urgency colors based on time remaining.
 *
 * @see AC-4.2.7.5 - Countdown timer shows urgency colors
 *
 * @module components/governance/VotingHeader
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { getUrgencyColorClass, formatTimeRemaining } from '../../utils/votingHelpers';

// =============================================================================
// Constants
// =============================================================================

/** Timer update interval in milliseconds (1 minute) */
const TIMER_UPDATE_INTERVAL_MS = 60000;

// =============================================================================
// Types
// =============================================================================

export interface VotingHeaderProps {
  /** Proposal title to display */
  title: string;
  /** Proposal ID for back navigation */
  proposalId: number;
  /** Voting end timestamp (Unix milliseconds) */
  votingEndsAt: number;
  /** Custom back URL (defaults to /proposals/:id) */
  backUrl?: string;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Voting header with back link, title, and countdown timer
 */
export function VotingHeader({
  title,
  proposalId,
  votingEndsAt,
  backUrl,
}: VotingHeaderProps) {
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Update timer every minute with defensive cleanup
  useEffect(() => {
    // Clear any existing interval first (defensive for strict mode)
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setNow(Date.now());
    }, TIMER_UPDATE_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleBack = () => {
    navigate(backUrl || `/proposals/${proposalId}`);
  };

  const urgencyClass = getUrgencyColorClass(votingEndsAt, now);
  const timeText = formatTimeRemaining(votingEndsAt, now);
  const isEnded = votingEndsAt <= now;

  return (
    <div className="mb-6">
      {/* Back link */}
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
        data-testid="voting-header-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to proposal
      </button>

      {/* Title and timer */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-xl font-semibold text-foreground" data-testid="voting-header-title">
          Vote on: {title}
        </h1>

        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 ${urgencyClass}`}
          data-testid="voting-header-timer"
        >
          <Clock className="w-4 h-4" />
          <span className="text-sm font-medium" data-testid="voting-header-time-text">
            {timeText}
          </span>
          {isEnded && (
            <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-0.5 rounded">
              Closed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default VotingHeader;
